// Libs
import classNames from 'classnames/bind';
import { Box, Button, Stack } from '@mui/material';
import { TFunction } from 'i18next';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import { useParams } from 'react-router-dom';
// Components, Layouts, Pages
import {
  BaseStatus,
  BaseTable,
  BaseTableOption,
  BaseTableUser,
  BaseToolbar,
  ConfirmModal,
} from '~/components/common';
import { FormJobTaskModal } from '~/components/module/job';
// Others
import { DateFormatEnum, TypeTabCreateJobEnum } from '~/utils/enum/common';
import { IHeaderCellType } from '~/utils/interface/common';
import { formatDate, generateUniqueId } from '~/utils/helper/common';
import { EMPTY_STRING } from '~/utils/constants/common';
import { IFormJob, IFormJobTask, ITaskJob } from '~/utils/interface/job';
// Styles, images, icons
import styles from './CreateJobTask.module.scss';
import { icons } from '~/assets';

type Props = {
  onEdit?: (data: IFormJob, type: string) => void;
  onCancelEdit: () => void;
};

const cx = classNames.bind(styles);

const columns = (t: TFunction, onDelete: (id?: string) => void): IHeaderCellType<IFormJobTask>[] => {
  return [
    {
      id: 'title',
      label: t('table_head_title_name'),
      render: (record) => record?.title || EMPTY_STRING,
    },
    {
      id: 'assignee',
      label: t('table_head_title_assignee'),
      render: (record) => {
        const { assignee } = record;
        if (!assignee) return;

        const { avatarImage, firstName, lastName } = assignee;
        return <BaseTableUser user={{ avatar: avatarImage, firstName, lastName }} />;
      },
    },
    {
      id: 'date',
      label: t('table_head_title_due_date'),
      render: (record) => {
        return record?.dueDate ? formatDate(record?.dueDate, DateFormatEnum.DD_MMM_YYYY) : EMPTY_STRING;
      },
    },
    {
      id: 'status',
      label: t('table_head_title_status'),
      render: (record) => (record?.status ? <BaseStatus label={record?.status} /> : EMPTY_STRING),
    },
    {
      id: 'action',
      label: t('table_head_title_action'),
      align: 'left',
      render: (record) => {
        return <BaseTableOption onDelete={() => onDelete(record.id)} />;
      },
    },
  ];
};

const CreateJobTask = (props: Props) => {
  //#region Destructuring Props
  const { onEdit, onCancelEdit } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/task']);
  const { id: jobId } = useParams();
  const {
    control,
    setValue,
    getValues,
    formState: { isDirty },
  } = useFormContext<IFormJob>();

  const customerId = useWatch({ control, name: 'clientId' });
  const tasks = useWatch({ control, name: 'tasks' });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [taskList, setTaskList] = useState<IFormJobTask[]>([]);
  const [dataIdTask, setDataIdTask] = useState<string>(EMPTY_STRING);
  const [isOpenConfirm, setIsOpenConfirm] = useState<boolean>(false);
  const [isOpenAddTask, setIsOpenAddTask] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (jobId) {
      const newTask: IFormJobTask[] =
        tasks?.map((item) => ({
          id: item?.id || generateUniqueId(),
          title: item?.title || EMPTY_STRING,
          dueDate: item?.dueDate || EMPTY_STRING,
          assigneeId: item?.assigneeId || EMPTY_STRING,
          description: item?.description || EMPTY_STRING,
          status: item?.status || EMPTY_STRING,
          assignee: item?.assignee,
        })) || [];

      setTaskList(newTask);
    }
  }, [jobId, tasks]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleDeleteTask = (id?: string) => {
    if (!id) return;

    setDataIdTask(id);
    setIsOpenConfirm(true);
  };

  const handleOpenFormTask = () => {
    setIsOpenAddTask(true);
  };

  const handleCloseModalFormTask = () => {
    setIsOpenAddTask(false);
  };

  const handleCloseConfirmDelete = () => {
    setIsOpenConfirm(false);
  };

  const handleConfirmDelete = () => {
    const newTask: IFormJobTask[] = taskList.filter((task) => task.id !== dataIdTask);

    setTaskList(newTask);

    const data: ITaskJob[] = newTask?.map((item) => ({
      title: item?.title,
      assigneeId: item?.accountId,
      dueDate: item?.dueDate,
      description: item?.description,
      status: item?.status,
      ...(item?.id && { id: item?.id }),
    }));

    setValue('tasks', data, { shouldValidate: true, shouldDirty: true });
    setIsOpenConfirm(false);
  };

  const handleSaveForm = (data?: IFormJobTask) => {
    if (!data) return;

    const newTask: IFormJobTask = {
      id: generateUniqueId(),
      ...data,
    };

    setTaskList((prev) => [...prev, newTask]);

    const dataNewTask: ITaskJob = {
      title: data?.title,
      assigneeId: data?.accountId,
      dueDate: data?.dueDate,
      description: data?.description,
      status: data?.status,
    };
    setValue('tasks', [...taskList, dataNewTask], { shouldValidate: true, shouldDirty: true });
    setIsOpenAddTask(false);
  };

  const handleSubmitFormSchedule = () => {
    const dataForm = getValues();
    const filteredTasks = (dataForm?.tasks || []).map(({ assignee, status, ...rest }) => rest);

    const dataBody: IFormJob = {
      name: EMPTY_STRING,
      address: EMPTY_STRING,
      clientId: EMPTY_STRING,
      tasks: filteredTasks || [],
    };

    onEdit && onEdit(dataBody, TypeTabCreateJobEnum.TASK);
  };
  //#endregion Handle Function

  return (
    <Box component={'div'} id='createJobTaskTabComponent' className={cx('container')}>
      <BaseToolbar textAction={t('button_add_task')} onAction={handleOpenFormTask} />

      <Box component={'div'} className={cx('body')}>
        <BaseTable columns={columns(t, handleDeleteTask)} dataSource={taskList || []} />
      </Box>
      {jobId && (
        <Stack
          direction={'row'}
          alignItems={'center'}
          justifyContent={'flex-end'}
          component={'div'}
          spacing={'12px'}
          className={cx('formFooter')}
        >
          <Button variant='outlined' onClick={onCancelEdit}>
            {t('common_action_cancel', { ns: 'common/base' })}
          </Button>

          <Button variant='contained' type='button' onClick={handleSubmitFormSchedule} disabled={!isDirty}>
            {t('common_action_save', { ns: 'common/base' })}
          </Button>
        </Stack>
      )}
      {isOpenAddTask && (
        <FormJobTaskModal
          customerId={customerId}
          onClose={handleCloseModalFormTask}
          onCallBack={handleSaveForm}
        />
      )}

      {isOpenConfirm && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}
    </Box>
  );
};

export default CreateJobTask;
